export interface Props{
	LangCount?: number;
	VerseOrder?: string[];
	Title?: string[];
	Font?: string[];
	BackgroundColor?: {rgb: number};
	BackgroundImage?: string;
	Comments?: string;
	Chords?: string[];
	Keywords?: string[];
	Categories?: string[];
	Version?: number;
	FontSize?: number;
	Tempo?: number;
	ChurchSongID?: string;
	CCLI?: string;
	Lang?: string[]|string;
	Songbook?: string;
	Bible?: string;
	Editor?: string;
	Melody?: string;
	Author?: string;
	Format?: string;
	TitleFormat?: string;
	NatCopyright?: string;
	Translation?: string;
}

export type Line = string[];
export type Slide = Line[];
export type Verse = Slide[];
export interface Texts {
	[verse: string]: Verse;
}

export interface Song {
	props: Props;
	texts: Texts;
}

export function sng2json(data: string): Song{
	const lines = data.trim().split('\r\n');
	let textBegin = lines.map(ln => ln.trim()).indexOf('---');
	{
		let altTextBegin = lines.indexOf('--');
		if(altTextBegin > 0 && textBegin > altTextBegin)
			textBegin = altTextBegin;
		if(textBegin === -1){
			throw new Error("Could not find text part in sng data!", {cause: {data: lines}});
		}
	}
	let props = parseProperties(lines.slice(0, textBegin));
	if(props === false)
		throw new Error("Could not parse properties of sng data!", {cause: {data: lines}});
	if(!props['LangCount'])
		props['LangCount'] = 1;
	
	if(!props['VerseOrder']){
		props['VerseOrder'] = [];
	}
	
	const songtext = lines.slice(textBegin);
	// Removes empty lines and lines with verse seperators from end of songtext
	while(songtext.at(-1)?.trim().length === 0 || ['---', '--'].indexOf(songtext.at(-1)?.trim() as any) > -1){
		songtext.pop();
	}
	const texts = parseSongtext(songtext, props);
	return {props: props, texts: texts};
}

const tags = ["Unbekannt", "Unbennant", "Unknown", "Intro", "Intro", "Vers", "Verse", "Strophe", "Pre-Bridge",
	"Bridge", "Misc", "Pre-Refrain", "Refrain", "Pre-Chorus", "Chorus", "Pre-Coda", "Zwischenspiel", "Interlude",
	"Coda", "Ending", "Teil", "Part"
];
function parseTag(tag: string): string|false {
	if(tag.startsWith('$$')){
		let matches = /\$\$[A-Za-z]=([\u00C0-\u017Fa-zA-z 0-9]+ ?(\([A-Za-z]\))?)/.exec(tag);
		if(!matches || matches.length < 2)
			throw new Error("Invalid userdefined tag!", {cause: { tag: tag }});
		return matches[1];
	}else if(tags.indexOf(tag) > -1){
		return tag;
	}
	return false;
}

function parseSongtext(lines: string[], props: Props): Texts {
	// Parse Lines into Parts
	let verse = '';
	let slide = 0;
	const verses: Record<string, string[][]> = {};
	while(lines.length){
		if(lines[0].trim() === '---'){
			if(lines[1] === '--'){ // Prevent empty slides / verses
				lines[1] = lines[0];
				continue;
			}else if(lines[1].trim() === '---'){
				lines.shift();
				continue;
			}
			if(props['VerseOrder'] && props['VerseOrder'].indexOf(parseTag(lines[1]) as any) >= 0 ){ // New verse with predefined header
				verse = lines[1];
				slide = 0;
				verses[verse] = [[]];
				lines.shift();
			}else{ // New Verse but no headers are defined
				const tag = parseTag(lines[1]);
				if(!props['VerseOrder']){
					props['VerseOrder'] = [];
				}
				if(tag !== false){ // If valid tag is specified
					props['VerseOrder'].push(tag);
					verse = tag;
					lines.shift();
				}else{ // Else first line is used as title
					verse = lines[1];
					props['VerseOrder'].push(lines[1]);
				}
				verses[verse] = [[]];
				slide = 0;
			}
		}else if(lines[0] === '--'){
			slide++;
			verses[verse][slide] = [];
		}else{
			verses[verse][slide].push(lines[0]);
		}
		lines.shift();
	}
	
	const texts: Texts = {};
	const LangCount = props['LangCount'] || 1;
	// Put each line in a array, if there are multiple languages, put multiple lines in those arrays
	for(var a in verses){ // Iterate through verses
		for(var b in verses[a]){ // Iterate through all slides in verse
			let newSlide: string[][] = [];
			for(let c = 0; c < verses[a][b].length; c++){ // Iterate through all lines in slide
				if(c % LangCount === 0)
					newSlide.push([]);
				newSlide[(c / LangCount) << 0][c % LangCount] = verses[a][b][c];
			}
			if(!texts[a])
				texts[a] = [];
			texts[a][b] = newSlide;
		}
	}

	return texts;
}

function parseProperties(lines: string[]): Props|false {
	const props: Props = {};
	for(let ln of lines){
		if(ln.trim().length === 0)
			continue;
		if(!ln.startsWith('#')){
			throw new Error("Malformed property (does not begin with #)!", {cause: {ln: ln, data: lines}});
		}
		const parts = /#(\([A-Za-z0-9]\)|[A-Za-z0-9]+)=(.*)/.exec(ln);
		if(!parts || parts.length !== 3){
			//throw {msg: "Malformed property!", data:{ln: ln, parts: parts, data: data}};
			continue;
		}
		let propname = parts[1] as (keyof Props|string);
		let val: any = parts[2];

		if(propname === 'Title'){
			val = [val];
		}else if(propname.startsWith('TitleLang')){
			const index = parseInt(propname.slice('TitleLang'.length));
			propname = "Title";
			const tmpval = val;
			if(!props["Title"])
				props["Title"] = [];
			val = props["Title"];
			val[index] = tmpval;
		}else if(propname === 'Font'){
			val = [val];
		}else if(propname.startsWith('FontLang')){
			var index = parseInt(propname.substr('FontLang'.length));
			propname = "Font";
			const tmpval = val;
			val = props["Font"] || [];
			val[index] = tmpval;
		}else if(propname === 'VerseOrder'){
			val = val.split(',');
		}else if(propname === 'Comments'){
			val = atob(val);
		}else if(propname === 'Chords'){
			val = atob(val).split("\r");
		}else if(propname === 'Keywords'){
			val = val.split(" ");
		}else if(propname === 'Categories'){
			val = val.split(",");
		}else if(propname === 'BackgroundImage' && val.startsWith('color://')){
			propname = 'BackgroundColor';
			const regex = /color:\/\/\$([0-9a-fA-F]{8})/;
			const matches = regex.exec(val);
			if(matches?.length !== 2){
				console.warn("Malformed Property");
				continue;
			}
			val = parseInt(matches[1], 16);
			val = {rgb: val & 0xFFFFFF}
		}else if(propname === '(c)'){
			propname = 'Copyright';
			val = val.split('|');
		}
		if(['Version', 'FontSize', 'Tempo', 'LangCount', 'ChurchSongID', 'CCLI'].includes(propname)){
			val = parseInt(val);
		}
		if(Array.isArray(val)){
			for(var i in val) 
				val[i] = val[i].trim();
		}else if(typeof val === 'string'){
			val = val.trim();
		}
		props[propname as keyof Props] = val;
	}
	/*if(props['Lang']){
		props['Lang'] = [props['Lang'] as string];
		if(props['LangCount'] && props['LangCount'] > 1){
			if(props['Lang'][0] === 'de')
				props['Lang'][1] == 'en';
			else
				props['Lang'][1] == 'de';
			props['Lang'][0] = 'en';
		}
	}*/
	return props;
}

export function renderSongTexts(song: Song, langs?: number[]){
	if(!langs){
		langs = Array.from(Array(song.props.LangCount).keys());
	}
	const langNames: string[] = [];
	const verses = song.props['VerseOrder'] || Object.keys(song.texts);
	const result: string[] = [];
	for(const verse of verses){
		for(const slide of song.texts[verse]){
			result.push(`<div class="verse" data-verse="${verse}">`);
			result.push(`<h2>${verse}</h2>`);
			for(const line of slide){
				result.push('<div class="slide">');
				for(const l of langs){
					if(line[l] === undefined){
						continue;
					}
					const ln = langNames[l];
					result.push(`<p class="line lang-${ln}">${line[l]}</p>`);
				}
				result.push('</div>');
			}
		}
	}
	return result.join('\n');
}
